/**
 * Advertisement API services
 */

import authorizedRequest from "@/lib/axios/authorizedRequest"
import { AttachmentType } from "@/constants/attachment-type"
import type { AdvertisementStatusValue } from "@/constants/advertisement-status"
import type { PolicyRuleApi } from "./policy"

// API Response Types
export interface AttachmentObject {
  id: string
  url: string
  attachment_type?: AttachmentType
}

/** Advertisement metrics from list API */
export interface AdvertisementMetrics {
  total_reach: number
  total_click_ad: number
  ctr: number
}

/** Minimal policy info embedded in advertisement list */
export interface AdvertisementPolicyItem {
  id: string
  name: string
  title: string
  chain_id?: string | number
  contract_address?: string
  rules?: PolicyRuleApi[]
  status: string
}

export interface AdvertisementResponse {
  id: string
  catchcopy?: string
  description?: string
  redirect_url?: string
  banner_id?: string | null
  sponsor_logo_id?: string | null
  banner?: AttachmentObject | null
  sponsor_logo?: AttachmentObject | null
  creator_id: number
  status: AdvertisementStatusValue
  created_at: string
  updated_at: string
  watch_time?: number | null
  rejected_reason?: string | null
  ad_type?: string
  /** Metrics for list view (reach, clicks, CTR) */
  metrics?: AdvertisementMetrics
  /** Policies linked to this ad (for list view) */
  policies?: AdvertisementPolicyItem[]
}

export interface PaginatedResponse<T> {
  status: boolean
  data: T[]
  meta: {
    current_page: number
    next_page: number | null
    prev_page: number | null
    total_pages: number
    total_count: number
  }
}

export interface AdvertisementDetailResponse {
  status: boolean
  data: AdvertisementResponse
}

export interface AdvertisementListResponse extends PaginatedResponse<AdvertisementResponse> {}

/**
 * Get list of advertisements with pagination
 */
export async function getAdvertisements(params?: {
  page?: number
  per?: number
  keyword?: string
  chain_id?: string
  status?: string
}): Promise<AdvertisementListResponse> {
  const queryParams = new URLSearchParams()
  if (params?.page) {
    queryParams.append("page", params.page.toString())
  }
  if (params?.per) {
    queryParams.append("per", params.per.toString())
  }
  if (params?.keyword) {
    queryParams.append("keyword", params.keyword)
  }
  if (params?.chain_id) {
    queryParams.append("chain_id", params.chain_id)
  }
  if (params?.status) {
    queryParams.append("status", params.status)
  }

  const url = `/v1/advertisements${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`
  const response = await authorizedRequest.get<AdvertisementListResponse>(url)
  return response as unknown as AdvertisementListResponse
}

/**
 * Get advertisement by ID
 */
export async function getAdvertisementById(
  id: string
): Promise<AdvertisementDetailResponse> {
  const response = await authorizedRequest.get<AdvertisementDetailResponse>(
    `/v1/advertisements/${id}`
  )
  return response as unknown as AdvertisementDetailResponse
}

/**
 * Create advertisement request type
 */
export interface CreateAdvertisementRequest {
  catchcopy?: string
  description?: string
  redirect_url?: string
  banner_id?: string
  sponsor_logo_id?: string
  watch_time?: number
}

/**
 * Create a new advertisement
 */
export async function createAdvertisement(
  data: CreateAdvertisementRequest
): Promise<AdvertisementDetailResponse> {
  const response = await authorizedRequest.post<AdvertisementDetailResponse>(
    "/v1/advertisements",
    data
  )
  return response as unknown as AdvertisementDetailResponse
}

/**
 * Update advertisement request type
 */
export interface UpdateAdvertisementRequest {
  catchcopy?: string
  description?: string | null
  redirect_url?: string | null
  banner_id?: string | null
  sponsor_logo_id?: string | null
  watch_time?: number
}

/**
 * Update advertisement by ID
 */
export async function updateAdvertisement(
  id: string,
  data: UpdateAdvertisementRequest
): Promise<AdvertisementDetailResponse> {
  const response = await authorizedRequest.patch<AdvertisementDetailResponse>(
    `/v1/advertisements/${id}`,
    data
  )
  return response as unknown as AdvertisementDetailResponse
}

export interface DeleteAdvertisementResponse {
  status: boolean
  data: {
    success: boolean
  }
}

/**
 * Delete advertisement by ID (soft delete)
 */
export async function deleteAdvertisement(
  id: string
): Promise<DeleteAdvertisementResponse> {
  const response = await authorizedRequest.delete<DeleteAdvertisementResponse>(
    `/v1/advertisements/${id}`
  )
  return response as unknown as DeleteAdvertisementResponse
}

/** Advertisement stats API types */
export interface AdvertisementStatsMetrics {
  reach_today: number
  click_today: number
  total_reach: number
  total_click: number
  ctr: number
}

export interface AdvertisementSponsorshipByDay {
  date: string
  times_used: number
  total_cost_usd: string
}

export interface AdvertisementStatsResponse {
  status: boolean
  data: {
    metrics: AdvertisementStatsMetrics
    sponsorship_by_day: AdvertisementSponsorshipByDay[]
  }
}

export interface GetAdvertisementStatsParams {
  from_date?: string
  to_date?: string
}

/**
 * Get advertisement statistics (metrics + per-day sponsorship)
 * GET /v1/advertisements/:id/stats
 */
export async function getAdvertisementStats(
  id: string,
  params?: GetAdvertisementStatsParams
): Promise<AdvertisementStatsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.from_date) {
    queryParams.append("from_date", params.from_date)
  }
  if (params?.to_date) {
    queryParams.append("to_date", params.to_date)
  }
  const query = queryParams.toString()
  const url = query
    ? `/v1/advertisements/${id}/stats?${query}`
    : `/v1/advertisements/${id}/stats`
  const response = await authorizedRequest.get<AdvertisementStatsResponse>(url)
  return response as unknown as AdvertisementStatsResponse
}
